import { RATE_SCALE, money, ratio, type Money, type Rate, type Ratio } from '@radish/primitives';
import { round, type Rounding } from './rounding.js';

/**
 * Exact fractions of an order and the money they map to. Pricing stages (e.g. the program stage's tender
 * settlement) track "what share of the order is paid" as an exact Ratio and round only when a share becomes money.
 */

export const RATIO_ZERO: Ratio = ratio(0n);
export const RATIO_ONE: Ratio = ratio(1n);

export const addRatio = (a: Ratio, b: Ratio): Ratio => ratio(a.num * b.den + b.num * a.den, a.den * b.den);
export const subtractRatio = (a: Ratio, b: Ratio): Ratio => ratio(a.num * b.den - b.num * a.den, a.den * b.den);
export const multiplyRatio = (a: Ratio, b: Ratio): Ratio => ratio(a.num * b.num, a.den * b.den);
export const compareRatio = (a: Ratio, b: Ratio): -1 | 0 | 1 => {
  const d = a.num * b.den - b.num * a.den;
  if (d === 0n) return 0;
  return d < 0n ? -1 : 1;
};
export const minRatio = (a: Ratio, b: Ratio): Ratio => (compareRatio(a, b) <= 0 ? a : b);
export const isZeroRatio = (a: Ratio): boolean => a.num === 0n;

/** `part / whole` as an exact ratio. `whole` must be non-zero (a programming error otherwise). */
export const fractionOf = (part: Money, whole: Money): Ratio => {
  if (part.currency !== whole.currency) throw new RangeError('fractionOf: currency mismatch');
  return ratio(part.minor, whole.minor);
};

/** `m × r` for an exact ratio, rounded once with the named mode. */
export const applyRatio = (m: Money, r: Ratio, mode: Rounding): Money =>
  money(round(ratio(m.minor * r.num, r.den), mode), m.currency);

/** Rounds `Σ weight_i × amount_i` once. Used to blend per-mode amounts by the share of the order paid in each mode. */
export const blend = (parts: readonly { weight: Ratio; amount: Money }[], mode: Rounding, currency: Money['currency']): Money => {
  const total = parts.reduce<Ratio>((acc, p) => addRatio(acc, ratio(p.amount.minor * p.weight.num, p.weight.den)), RATIO_ZERO);
  return money(round(total, mode), currency);
};

/** Pre-tax part of a tax-inclusive gross: `gross / (1 + r)`, rounded once (S3 inclusive rule). */
export const inclusivePreTax = (gross: Money, r: Rate, mode: Rounding): Money =>
  money(round(ratio(gross.minor * RATE_SCALE, RATE_SCALE + r.scaled), mode), gross.currency);

/** Rounds to a multiple of `increment` minor units (100 = whole dollars) with the named mode. */
export const roundToIncrement = (m: Money, increment: bigint, mode: Rounding): Money => {
  if (increment <= 0n) throw new RangeError('roundToIncrement: increment must be positive');
  return money(round(ratio(m.minor, increment), mode) * increment, m.currency);
};

/** True when `|amount| ≤ share × |base|`, compared exactly. */
export const withinShare = (amount: Money, base: Money, share: Rate): boolean => {
  const a = amount.minor < 0n ? -amount.minor : amount.minor;
  const b = base.minor < 0n ? -base.minor : base.minor;
  return a * RATE_SCALE <= share.scaled * b;
};

export const minMoney = (a: Money, b: Money): Money => (a.minor <= b.minor ? a : b);
export const maxMoney = (a: Money, b: Money): Money => (a.minor >= b.minor ? a : b);
export const absMoney = (a: Money): Money => (a.minor < 0n ? money(-a.minor, a.currency) : a);

/** `m × r` as an exact ratio (no rounding): the pricing stages round it at a named step (A6). */
export const timesRatio = (m: Money, r: Ratio): Ratio => ratio(m.minor * r.num, r.den);

/** `m / d` as an exact ratio. `d` must be non-zero. */
export const dividedBy = (m: Money, d: bigint): Ratio => ratio(m.minor, d);

/** Pre-tax part of a tax-inclusive gross, `gross / (1 + r)`, exact (rounded by the caller at a named step). */
export const inclusivePreTaxExact = (gross: Money, r: Rate): Ratio =>
  ratio(gross.minor * RATE_SCALE, RATE_SCALE + r.scaled);
